import ReactSelect, {
  MultiValue,
  PropsValue,
  SingleValue,
  type StylesConfig,
} from "react-select";
import { useQuery } from "@tanstack/react-query";

import { getSelectData } from "src/api/reportApi";

type StaticSelectOption = {
  label: string;
  value: string | number;
};

type StaticSelectProps = {
  onChange: (value: string | number | Array<string | number>) => void;
  id?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  api_endpoint?: string;
  static_options?: StaticSelectOption[];
  isMulti?: boolean;
  renderMenuInPortal?: boolean;
  formValues?: Record<string, string | number>;
  defaultValues?: Record<string, string | number>;
  onMenuOpen?: () => void;
};

export default function Select({
  onChange,
  id = "staticSelect",
  name = "staticSelect",
  className,
  disabled,
  placeholder,
  api_endpoint,
  static_options,
  formValues,
  defaultValues,
  isMulti = false,
  renderMenuInPortal = false,
  onMenuOpen,
}: StaticSelectProps) {
  const { isLoading, data: options } = useQuery<StaticSelectOption[], Error>({
    queryKey: ["entity", id],
    queryFn: () => getSelectData(api_endpoint as string),
    enabled: !!api_endpoint,
  });
  const menuPortalTarget =
    renderMenuInPortal && typeof document !== "undefined"
      ? document.body
      : undefined;
  const selectStyles: StylesConfig<StaticSelectOption, boolean> =
    renderMenuInPortal
      ? {
          menuPortal: (base) => ({
            ...base,
            zIndex: 2000,
          }),
          menu: (base) => ({
            ...base,
            zIndex: 2000,
          }),
        }
      : {};

  const onChangeSelect = (
    option:
      | SingleValue<StaticSelectOption>
      | MultiValue<StaticSelectOption>,
  ) => {
    onChange(
      Array.isArray(option)
        ? option.map((entry) => entry.value)
        : ((option as unknown as { value: string | number })?.value ?? ""),
    )
  }

  const calculateSelectValue = () => {
    if(formValues?.[id] || defaultValues?.[id]) {
      return {
        value:
          formValues?.[id] ??
          defaultValues?.[id],
        label:
          static_options && static_options.length > 0 ? 
            static_options?.find(
              (option) =>
                option.value ===
                (formValues?.[id] ??
                  defaultValues?.[id]),
            )?.label ?? "" :
            options?.find(
              (option) =>
                option.value ===
                (formValues?.[id] ??
                  defaultValues?.[id]),
            )?.label ?? ""
      } as unknown as PropsValue<StaticSelectOption>
    }
    return null;
  }

  return (
    <ReactSelect<StaticSelectOption, boolean>
      inputId={id}
      name={name}
      className={className}
      value={calculateSelectValue()}
      options={api_endpoint ? options : (static_options ?? [])}
      onChange={onChangeSelect}
      isDisabled={disabled}
      isClearable={false}
      placeholder={placeholder}
      formatOptionLabel={(option) =>
        option.label.length > 0
          ? option.label.charAt(0).toUpperCase() + String(option.label).slice(1)
          : option.label
      }
      isLoading={isLoading}
      isMulti={isMulti}
      onMenuOpen={onMenuOpen}
      menuPortalTarget={menuPortalTarget}
      menuPosition={renderMenuInPortal ? "fixed" : "absolute"}
      menuShouldScrollIntoView={!renderMenuInPortal}
      styles={selectStyles}
    />
  );
}
